"use client";

import { useState, useEffect, useRef } from "react";
import { ChevronDown, Check } from "lucide-react";

/**
 * Selector de filtro en forma de píldora. Vivía dentro de
 * `TransparenciaComponent`; se extrajo cuando la página de Información
 * Financiera necesitó el mismo control sobre el subartículo 10.2.
 */
export default function FilterDropdown({
  label,
  value,
  options,
  onChange,
  allLabel,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  allLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const displayValue = value === "all" ? allLabel : selected?.label ?? allLabel;
  const isActive = value !== "all";

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center justify-between gap-3 min-w-[200px] px-4 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors border ${
          isActive
            ? "bg-[#102542] text-white border-[#102542]"
            : "bg-white text-gray-700 border-gray-200 hover:border-[#102542]"
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 truncate">
          <span
            className={`opacity-70 ${isActive ? "text-white/80" : "text-gray-400"}`}
          >
            {label}:
          </span>
          <span className="truncate normal-case tracking-normal font-medium">
            {displayValue}
          </span>
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute top-full left-0 mt-2 z-40 min-w-full max-w-xs max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-2xl shadow-lg py-2"
        >
          <button
            onClick={() => {
              onChange("all");
              setOpen(false);
            }}
            className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between transition-colors ${
              value === "all"
                ? "text-[#102542] font-semibold bg-gray-50"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            <span>{allLabel}</span>
            {value === "all" && <Check className="w-4 h-4" />}
          </button>
          <div className="my-1 border-t border-gray-100" />
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between transition-colors ${
                value === opt.value
                  ? "text-[#102542] font-semibold bg-gray-50"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span className="truncate">{opt.label}</span>
              {value === opt.value && (
                <Check className="w-4 h-4 flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
